import { useEffect, useRef, useState, type ReactNode } from 'react';
import './scrollable-calendar-list.css';

export function ScrollableCalendarList({ children }: { children: ReactNode }) {
  const list = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  const [above, setAbove] = useState(false);
  const measure = () => {
    const element = list.current;
    if (!element) return;
    setMore(element.scrollTop + element.clientHeight < element.scrollHeight - 2);
    setAbove(element.scrollTop > 2);
  };
  useEffect(() => {
    const element = list.current;
    const sidebar = element?.closest<HTMLElement>('.calendar-sidebar');
    const surface = sidebar?.parentElement?.querySelector<HTMLElement>('.calendar-surface');
    if (!element || !sidebar || !surface) return;
    let frame = 0;
    const size = () => {
      if (sidebar.getBoundingClientRect().height === 0) return;
      const top = Math.max(16, sidebar.getBoundingClientRect().top);
      sidebar.style.setProperty(
        '--calendar-sidebar-height',
        `${Math.max(240, Math.min(window.innerHeight - top - 16, surface.getBoundingClientRect().height))}px`,
      );
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(size);
    observer.observe(sidebar);
    observer.observe(element);
    observer.observe(surface);
    size();
    window.addEventListener('resize', size);
    window.addEventListener('scroll', size, { passive: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', size);
      window.removeEventListener('scroll', size);
    };
  }, []);
  useEffect(measure, [children]);
  return (
    <div className="calendar-list-shell" data-more={more} data-above={above}>
      <div
        className="calendar-filter-list"
        ref={list}
        onScroll={measure}
        role="region"
        aria-label="Calendar list"
        tabIndex={0}
      >
        {children}
      </div>
      <div className="calendar-list-cue">
        {more && (
          <button
            type="button"
            className="link-button"
            onClick={() =>
              list.current?.scrollBy({
                top: Math.max(80, list.current.clientHeight * 0.7),
                behavior: 'auto',
              })
            }
          >
            More calendars ↓
          </button>
        )}
      </div>
    </div>
  );
}
